import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useOutlet } from "@/contexts/OutletContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Clock, Users, X } from "lucide-react";

interface WaitlistRow {
  waitlist_id: string;
  listing_id: string;
  desired_date: string;
  desired_time: string | null;
  party_size: number;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  status: string;
  offer_expires_at: string | null;
  created_at: string;
  listings: { name: string } | null;
}

const STATUS_STYLE: Record<string, string> = {
  waiting: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  offered: "bg-sky-500/15 text-sky-700 border-sky-500/30",
  fulfilled: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  claimed: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
  expired: "bg-slate-100 text-slate-700 border-slate-300",
  cancelled: "bg-slate-100 text-slate-700 border-slate-300",
};

// TEMP: sample rows for UI preview while the waitlist is empty. Remove before commit.
const day = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const SAMPLE_ROWS: WaitlistRow[] = [
  { waitlist_id: "s1", listing_id: "l1", desired_date: day(1), desired_time: "10:00", party_size: 2, customer_name: "Amelia Tan", customer_email: "amelia@example.com", customer_phone: "+65 8123 4567", status: "waiting", offer_expires_at: null, created_at: day(-1), listings: { name: "Morning Yoga Flow" } },
  { waitlist_id: "s2", listing_id: "l2", desired_date: day(2), desired_time: null, party_size: 1, customer_name: "Daniel Lim", customer_email: "daniel@example.com", customer_phone: null, status: "offered", offer_expires_at: new Date(Date.now() + 5400000).toISOString(), created_at: day(-2), listings: { name: "Pottery Workshop" } },
  { waitlist_id: "s3", listing_id: "l1", desired_date: day(3), desired_time: "18:30", party_size: 4, customer_name: "Priya Nair", customer_email: null, customer_phone: "+65 9234 5678", status: "waiting", offer_expires_at: null, created_at: day(-1), listings: { name: "Sunset Kayak Tour" } },
  { waitlist_id: "s4", listing_id: "l3", desired_date: day(-1), desired_time: "09:00", party_size: 1, customer_name: "Marcus Wong", customer_email: "marcus@example.com", customer_phone: null, status: "claimed", offer_expires_at: null, created_at: day(-4), listings: { name: "Morning Yoga Flow" } },
  { waitlist_id: "s5", listing_id: "l2", desired_date: day(-2), desired_time: null, party_size: 3, customer_name: "Sofia Garcia", customer_email: "sofia@example.com", customer_phone: null, status: "expired", offer_expires_at: null, created_at: day(-5), listings: { name: "Pottery Workshop" } },
];

export default function WaitlistManagement() {
  const { businessId } = useAuth();
  const { activeOutlet } = useOutlet();
  const [rows, setRows] = useState<WaitlistRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true);
    // waitlist_entries isn't in the generated DB types yet. Cast to bypass.
    let q = (supabase as any)
      .from("waitlist_entries")
      .select("waitlist_id, listing_id, desired_date, desired_time, party_size, customer_name, customer_email, customer_phone, status, offer_expires_at, created_at, listings(name)")
      .eq("business_id", businessId)
      .order("desired_date", { ascending: true })
      .order("created_at", { ascending: true });
    if (activeOutlet?.outlet_id) q = q.eq("outlet_id", activeOutlet.outlet_id);
    const { data, error } = await q;
    if (error) {
      toast({ title: "Couldn't load waitlist", description: error.message, variant: "destructive" });
    } else {
      setRows(((data as WaitlistRow[]) ?? []).length ? (data as WaitlistRow[]) : SAMPLE_ROWS);
    }
    setLoading(false);
  }, [businessId, activeOutlet?.outlet_id]);

  useEffect(() => { load(); }, [load]);

  const visible = rows;

  const remove = async (id: string) => {
    const { error } = await (supabase as any)
      .from("waitlist_entries")
      .update({ status: "cancelled" })
      .eq("waitlist_id", id);
    if (error) {
      toast({ title: "Couldn't remove", description: error.message, variant: "destructive" });
      return;
    }
    setRows((prev) => prev.map((r) => (r.waitlist_id === id ? { ...r, status: "cancelled" } : r)));
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border rounded-xl">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No one on the waitlist right now.</p>
        </div>
      ) : (
        <div className="admin-data-table-shell">
          <div className="admin-data-table-scroll w-full overflow-x-auto bg-card">
            <Table className="admin-data-table" style={{ minWidth: 900 }}>
              <TableHeader className="admin-data-table-header">
                <TableRow className="admin-data-table-header-row">
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Listing</TableHead>
                  <TableHead>Date & time</TableHead>
                  <TableHead className="text-right">Party size</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Offer</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody className="admin-data-table-body">
                {visible.map((r) => (
                  <TableRow key={r.waitlist_id} className="admin-data-table-row">
                    <TableCell className="admin-data-table-cell font-medium">{r.customer_name}</TableCell>
                    <TableCell className="admin-data-table-cell">
                      <span className={cn("inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize", STATUS_STYLE[r.status])}>{r.status}</span>
                    </TableCell>
                    <TableCell className="admin-data-table-cell">{r.listings?.name ?? "Service"}</TableCell>
                    <TableCell className="admin-data-table-cell whitespace-nowrap">
                      {r.desired_date}{r.desired_time ? ` at ${r.desired_time}` : " (any time)"}
                    </TableCell>
                    <TableCell className="admin-data-table-cell text-right tabular-nums">{r.party_size}</TableCell>
                    <TableCell className="admin-data-table-cell text-muted-foreground">{r.customer_email ?? "—"}</TableCell>
                    <TableCell className="admin-data-table-cell whitespace-nowrap text-muted-foreground">{r.customer_phone ?? "—"}</TableCell>
                    <TableCell className="admin-data-table-cell whitespace-nowrap">
                      {r.status === "offered" && r.offer_expires_at ? (
                        <span className="flex items-center gap-1 text-blue-700">
                          <Clock className="h-3 w-3" />until {new Date(r.offer_expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="admin-data-table-cell">
                      {(r.status === "waiting" || r.status === "offered") && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(r.waitlist_id)} title="Remove from waitlist">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
